import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { parseDateToLocal } from '../../../../../../shared/utils';
import { surveyRowId } from '../../../../../../shared/surveys';
import CustomerCareDTable from '../../../../../Shared/CustomerCareDTable';
import NoDataCard from '../../../../../Dashboard/detailPanel/NoDataCard';
import SurveyResponseChip from '../SurveyResponseChip';
import { SurveyResponse } from '../../../../../../shared/constants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomerSurveyResponses } from '../../../../../../store/customers/actionCreators';
import LoadingSpinner from '../../../../../Shared/LoadingSpinner';
import type {
  CachedCustomerSurveyResponses,
  CustomerSurveyResponse,
} from '../../../../../../store/customers/customerTypes';

// Hub-experience (CSAT, 1-4 scale) survey history: survey_type_id=1 rows.
const CSATDetailsPanel = ({ customerId }: { customerId: number }) => {
  const dispatch = useDispatch();
  const { customerSurveyResponses, customerSurveyLoading } = useSelector(
    (state: any) => state.customers,
  ) as {
    customerSurveyResponses: CachedCustomerSurveyResponses | undefined;
    customerSurveyLoading: boolean;
  };

  useEffect(() => {
    // Only fetch if not already loaded for this customer
    if (
      !customerSurveyResponses ||
      (customerSurveyResponses as { customerId?: number }).customerId !==
        customerId
    ) {
      dispatch(fetchCustomerSurveyResponses(customerId));
    }
  }, [dispatch, customerId]);

  const columns = [
    {
      field: 'response',
      headerName: 'Survey response',
      flex: 2,
      renderCell: (params: any) => (
        <SurveyResponseChip
          response={params.row.response as SurveyResponse}
          isCurrent
        />
      ),
    },
    {
      field: 'received_at',
      headerName: 'Response time',
      flex: 2,
      renderCell: (params: any) => (
        <span>
          {params.row.received_at
            ? parseDateToLocal(params.row.received_at, 'yyyy-MM-dd HH:mm:ss')
            : 'N/A'}
        </span>
      ),
    },
  ];

  // Same cached shape as the Issues panel: an array, or the array spread into
  // an object plus `customerId`. Survey rows have no DB id, so derive one.
  const surveyRows: CustomerSurveyResponse[] = Array.isArray(
    customerSurveyResponses,
  )
    ? customerSurveyResponses
    : (Object.values(customerSurveyResponses || {}).filter(
        (item: any) => typeof item === 'object' && item.score !== undefined,
      ) as CustomerSurveyResponse[]);
  const rows = surveyRows.map((row) => ({ ...row, id: surveyRowId(row) }));

  if (customerSurveyLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={customerSurveyLoading}
            pageSize={4}
            defaultSortField="received_at"
            defaultSortDirection="desc"
            hideViewAllButton
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default CSATDetailsPanel;
